import { useEffect, useState } from 'react';
import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '../../../components/loader/Loader';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { toast } from 'react-toastify';
import DataTable from '../../../components/table/DataTable';
import {
  DocumentStatus,
  InvoiceBase,
  InvoiceBaseInputs,
  LinkedDocument,
} from '../../../types/Invoice';
import { columns } from '../../../components/columns/Company/LinkedDocumentsColumns';
import { paths } from '../../RouterContainer';
import SelectFilteredModalTable from '../../../components/select/SelectFilteredModalTable';
import { columns as invoiceColumns } from '../../../components/columns/Company/InvoiceOverviewColumns';
import Button from '../../../components/button/Button';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { useTranslation } from '../../../context/translationContext';
import { useLoading } from '../../../context/loadingContext';
import ListButton, { ListButtonEntityType } from '../../../components/listButton/ListButton';

interface Props {
  document: InvoiceBaseInputs;
  mutate?: () => void;
}
/**
 * Component for managing linked documents to current document
 */
export const InvoiceSidePanelTabLinkedDocuments = ({ document, mutate }: Props) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { company } = useCurrentCompany();
  const navigate = useNavigate();
  const [linkedDocuments, setLinkedDocuments] = useState<InvoiceBase[] | null>(null);
  const [filteredDocuments, setFilteredDocuments] = useState<InvoiceBase[] | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const { startLoading, stopLoading } = useLoading();
  /**
   * Loads linked documents to current document
   */
  const fetchLinkedDocuments = async () => {
    if (!id) return;
    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_LINKED_DOC_LIST}?documentId=${id}`,
    });
    if (response?.data) {
      let documents: InvoiceBase[] = [];
      // view child and parrent documents in table
      response?.data.map((i: LinkedDocument) => {
        // dont show current document
        if (i.parentDocument.id !== id)
          documents = [
            ...documents,
            { ...i.parentDocument, accountingDocumentOnAccountingDocumentId: i.id },
          ];
        if (i.childDocument.id !== id)
          documents = [
            ...documents,
            { ...i.childDocument, accountingDocumentOnAccountingDocumentId: i.id },
          ];
      });
      setLinkedDocuments(documents);
    }
  };

  /**
   * Loads list of invoices for current company
   * used in modal selection to link documents
   */
  const fetchDocuments = async () => {
    if (linkedDocuments === null) return;
    const req = `?companyId=${company?.id}`;
    // TODO filtry
    /*  if (filter.documentDirection) req += `&documentDirection=${filter.documentDirection}`;
    if (filter.documentType) req += `&documentType=${filter.documentType}`;
    if (filter.filterFrom) req += `&filterFrom=${format(filter.filterFrom, dateSubmitFormat)}`;
    if (filter.filterTo) req += `&filterTo=${format(filter.filterTo, dateSubmitFormat)}`; */

    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_LIST}${req}`,
    });
    if (response?.data) {
      // dont show already linked documents & dont show current document & dont show concepts (no document number)
      const filteredArr = response?.data.filter(
        (x: InvoiceBase) =>
          !linkedDocuments.find((y: InvoiceBase) => y.id === x.id) &&
          x.id !== id &&
          x.documentStatus !== DocumentStatus.CONCEPT,
      );
      setFilteredDocuments(filteredArr);
    }
  };

  useEffect(() => {
    fetchLinkedDocuments();
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchDocuments();
  }, [open]);

  /**
   * Submits message to chat
   */
  const handleLinkDocument = async (document: InvoiceBase) => {
    try {
      startLoading();
      const response = await http.apiPost({
        url: `${apiUrl.ACCOUNTING_DOCUMENT_LINKED_DOC_POST}?parentDocumentId=${id}&childDocumentId=${document.id}`,
      });
      if (response?.status === 200) {
        stopLoading();
        mutate && mutate();
        fetchLinkedDocuments();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * Unlinks selected document from current document
   * @param id of entity
   */
  const onRemoveItem = async (id: string | number) => {
    if (!id) return;
    const response = await http.apiDelete({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_LINKED_DOC_DELETE}?accountingDocumentOnAccountingDocumentId=${id}`,
    });
    if (response?.status === 204) {
      mutate && mutate();
      toast.success('Success!');
      fetchLinkedDocuments();
    }
  };

  /**
   * Open selected document detail
   */
  const onOpenDetail = (id: string | number) => {
    if (!id) return;
    navigate(`/${paths.INVOICE}/${id}`);
  };

  if (linkedDocuments === null) return <Loader />;
  return (
    <>
      <div className="flex justify-between items-center">
        <div>
          {document.connectionAttributes && (
            <ListButton
              entityType={ListButtonEntityType.CONNECTION_ATTRIBUTES}
              params={document.connectionAttributes}
            />
          )}
        </div>
        <div className="text-right">
          <Button title={t('page.invoice.btn-link-document')} onClick={() => setOpen(true)} />
        </div>
      </div>
      <div className="mt-5 text-center">
        <DataTable
          data={linkedDocuments}
          columns={columns()}
          handleRemove={onRemoveItem}
          removeIdKey="accountingDocumentOnAccountingDocumentId"
          customButtons={[
            {
              title: 'Detail',
              color: 'blue',
              onSubmit: (id) => onOpenDetail(id),
            },
          ]}
        />
        <SelectFilteredModalTable
          data={filteredDocuments ?? []}
          columns={invoiceColumns}
          open={open}
          onClose={() => setOpen(false)}
          labelKey="name"
          valueKey="id"
          showResult={false}
          onSelected={(val) => handleLinkDocument(val)}
          onChange={() => null}
          fulltextSearch
          inputClassName={`border-0`}
          modalClassName={`left-[48%]`}
        />
      </div>
    </>
  );
};
